import type { ReactNode } from "react";
import { cn } from "../lib/utils";
import { DefaultLink, type NavItem, type NavLinkComponent } from "./nav";

type LeftRailProps = {
	items: NavItem[];
	activeKey: string | undefined;
	label: string;
	header?: ReactNode;
	footer?: ReactNode;
	linkComponent?: NavLinkComponent;
	/** `rail`: icons over short labels, for the staff app. `sidebar`: wide rows, for admin. */
	variant?: "rail" | "sidebar";
	className?: string;
};

/** Left navigation for wide screens. Hidden under 768 px, where bottom tabs take over. */
export function LeftRail({
	items,
	activeKey,
	label,
	header,
	footer,
	linkComponent: Link = DefaultLink,
	variant = "rail",
	className,
}: LeftRailProps) {
	const isRail = variant === "rail";
	return (
		<aside
			data-slot="left-rail"
			className={cn(
				"sticky top-0 hidden h-dvh shrink-0 flex-col border-r bg-surface md:flex",
				isRail ? "w-24" : "w-60",
				className,
			)}
		>
			{header ? (
				<div className={cn("flex items-center p-3", isRail && "justify-center")}>{header}</div>
			) : null}
			<nav aria-label={label} className="flex-1 overflow-y-auto px-2 py-2">
				<NavList items={items} activeKey={activeKey} linkComponent={Link} variant={variant} />
			</nav>
			{footer ? (
				<div className={cn("border-t p-3", isRail && "flex justify-center")}>{footer}</div>
			) : null}
		</aside>
	);
}

export function NavBadge({ count, rail }: { count: number; rail?: boolean }) {
	return (
		<span
			className={cn(
				"min-w-5 rounded-chip bg-destructive px-1.5 text-center text-xs leading-5 font-semibold text-destructive-foreground tabular-nums",
				rail ? "absolute top-1 right-2" : "ml-auto",
			)}
		>
			{count > 99 ? "99+" : count}
		</span>
	);
}

type NavListProps = {
	items: NavItem[];
	activeKey: string | undefined;
	linkComponent?: NavLinkComponent;
	variant?: "rail" | "sidebar";
};

/** The list of navigation links, shared by the rail and the phone menu sheet. */
export function NavList({
	items,
	activeKey,
	linkComponent: Link = DefaultLink,
	variant = "sidebar",
}: NavListProps) {
	const isRail = variant === "rail";
	return (
		<ul className="flex flex-col gap-1">
			{items.map((item) => {
				const active = item.key === activeKey;
				const Icon = item.icon;
				return (
					<li key={item.key}>
						<Link
							href={item.href}
							aria-current={active ? "page" : undefined}
							className={cn(
								"relative flex rounded-button text-sm font-medium text-muted-foreground transition-colors outline-none hover:bg-accent hover:text-accent-foreground focus-visible:ring-3 focus-visible:ring-ring/50",
								isRail
									? "min-h-16 flex-col items-center justify-center gap-1 px-1 py-2 text-xs"
									: "min-h-10 items-center gap-3 px-3 pointer-coarse:min-h-12",
								active && "bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary",
							)}
						>
							<Icon className="size-5 shrink-0" aria-hidden />
							<span className={cn(isRail && "text-center leading-tight")}>{item.label}</span>
							{item.badge ? <NavBadge count={item.badge} rail={isRail} /> : null}
						</Link>
					</li>
				);
			})}
		</ul>
	);
}
